import { useCallback, useState } from "react";
import { Group, Text, Paper, Select, TextInput, ActionIcon, createStyles, Stack, Button, Anchor } from "@mantine/core";
import { SearchKeywordList, TicketEnquiryType, TicketStatus } from "./enquiryInbox.types";
import { useRouterContext } from "@hooks";
import { IconSearch } from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";

const useStyles = createStyles((theme) => ({
  searchContainer: {
    gap: 0,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
    borderRadius: 10,
    maxWidth: 250,
    padding: 3,
  },
}));

interface SearchTicketsCardProps {
  status?: TicketStatus;
  onStatusChange?: (val: string) => void;
  isEscalation?: boolean;
  onIsEscalationChange?: (val: boolean) => void;
  enquiryType?: string;
  onEnquiryTypeChange?: (val: string) => void;
  searchKeyword?: string;
  refreshTable?: () => void;
  fromDate?: Date;
  toDate?: Date;
  onFromDateChange?: (val: Date | undefined) => void;
  onToDateChange?: (val: Date | undefined) => void;
}

const statuses = [
  {
    value: "",
    label: "- All -",
  },
  {
    value: "Open",
    label: "New/Open",
  },
  {
    value: "Pending",
    label: "Pending",
  },
  {
    value: "Solved",
    label: "Solved",
  },
  {
    value: "Escalation",
    label: "All Unsolved",
  },
];

const enquiryTypes = [
  {
    value: "",
    label: "- All -",
  },
].concat(
  Object.keys(TicketEnquiryType)
    .map((enquiryType) => ({
      value: enquiryType,
      label: TicketEnquiryType[enquiryType as keyof typeof TicketEnquiryType],
    }))
    .sort((a, b) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0))
);

const generateSearchUrl = (
  isEscalation: boolean,
  searchKeyInput: string,
  status: string,
  enquiryType: string,
  fromDate?: Date | null,
  toDate?: Date | null
) => {
  // Build query parameters using URLSearchParams for cleaner code
  const queryParams = new URLSearchParams();

  if (status) {
    queryParams.append("status", status);
  }

  if (enquiryType) {
    queryParams.append("enquiryType", enquiryType);
  }

  // Convert date to ISO string format (YYYY-MM-DD) if provided
  if (fromDate) {
    queryParams.append("fromDate", dayjs(fromDate).format("YYYY-MM-DD"));
  }

  if (toDate) {
    queryParams.append("toDate", dayjs(toDate).format("YYYY-MM-DD"));
  }

  const queryString = queryParams.toString();
  return `/enquiryinbox/search-tickets/${isEscalation}/${searchKeyInput}${queryString ? `?${queryString}` : ""}`;
};

export const SearchTicketsCard = ({
  status: initialStatus,
  onStatusChange,
  isEscalation: initialIsEscalation,
  onIsEscalationChange,
  enquiryType: initialEnquiryType,
  onEnquiryTypeChange,
  searchKeyword: initialSearchKeyword,
  refreshTable,
  fromDate: initialFromDate,
  toDate: initialToDate,
  onFromDateChange,
  onToDateChange,
}: SearchTicketsCardProps) => {
  const { useHistory } = useRouterContext();
  const { classes, theme } = useStyles();
  const { push: navigate } = useHistory();

  const [status, setStatus] = useState<any>(initialStatus ? TicketStatus[initialStatus].toString() : "");
  const [searchKeyword, setSearchKeyword] = useState<string>(initialSearchKeyword || "");
  const [searchType, setSearchType] = useState<any>(
    initialIsEscalation || false
      ? SearchKeywordList.EscalatedTickets.toString()
      : SearchKeywordList.CustomerTickets.toString()
  );
  const [searchKeyInput, setSearchKeyInput] = useState<string>(
    initialSearchKeyword ? initialSearchKeyword.replace("#", "") : ""
  );
  const [isEscalation, setIsEscalation] = useState<boolean>(initialIsEscalation || false);
  const [enquiryType, setEnquiryType] = useState<any>(initialEnquiryType || "");
  const [fromDate, setFromDate] = useState<Date | null>(initialFromDate || null);
  const [toDate, setToDate] = useState<Date | null>(initialToDate || null);
  const [dateFiltersApplied, setDateFiltersApplied] = useState<boolean>(!!initialFromDate && !!initialToDate);
  const [fromDateError, setFromDateError] = useState<string>("");
  const [toDateError, setToDateError] = useState<string>("");

  // Validation function
  const validateDates = useCallback(() => {
    let isValid = true;
    const today = dayjs().endOf("day");

    // Clear previous errors
    setFromDateError("");
    setToDateError("");

    // Check if dates are provided
    if (!fromDate || !toDate) {
      return false;
    }

    // Validate fromDate is not in the future
    if (dayjs(fromDate).isAfter(today)) {
      setFromDateError("The From date cannot be in the future");
      isValid = false;
    }

    // Validate toDate is not in the future
    if (dayjs(toDate).isAfter(today)) {
      setToDateError("The To date cannot be in the future");
      isValid = false;
    }

    // Validate toDate is after fromDate
    if (fromDate && toDate && dayjs(toDate).isBefore(dayjs(fromDate))) {
      setToDateError("The To date must be after the From date.");
      isValid = false;
    }

    return isValid;
  }, [fromDate, toDate]);

  const ticketTypes = [
    {
      value: SearchKeywordList.CustomerTickets,
      label: SearchKeywordList.CustomerTickets.toString(),
    },
    {
      value: SearchKeywordList.EscalatedTickets,
      label: SearchKeywordList.EscalatedTickets.toString(),
    },
  ];

  const handleSearchKeywordChange = useCallback((event: any) => {
    var val = event.currentTarget.value;
    setSearchKeyword(val);
    setSearchKeyInput(val.replace("#", ""));
  }, []);

  const handleSearchTypeChange = useCallback(
    (val: any) => {
      const isEscalationNew = val?.toString() == SearchKeywordList.EscalatedTickets.toString();
      setSearchType(val?.toString());
      setIsEscalation(isEscalationNew);
      if (onIsEscalationChange) {
        onIsEscalationChange(isEscalationNew);
      }
      if (refreshTable) {
        refreshTable();
      }
      navigate(generateSearchUrl(isEscalationNew, searchKeyInput, status, enquiryType, fromDate, toDate));
    },
    [enquiryType, navigate, onIsEscalationChange, refreshTable, searchKeyInput, status, fromDate, toDate]
  );

  const handleStatusChange = useCallback(
    (val: any) => {
      setStatus(val);
      if (onStatusChange) {
        onStatusChange(val);
      }
      if (refreshTable) {
        refreshTable();
      }
      navigate(generateSearchUrl(isEscalation, searchKeyInput, val, enquiryType, fromDate, toDate));
    },
    [onStatusChange, refreshTable, navigate, isEscalation, searchKeyInput, enquiryType, fromDate, toDate]
  );

  const handleEnquiryTypeChange = useCallback(
    (val: any) => {
      setEnquiryType(val);
      if (onEnquiryTypeChange) {
        onEnquiryTypeChange(val);
      }
      if (refreshTable) {
        refreshTable();
      }
      navigate(generateSearchUrl(isEscalation, searchKeyInput, status, val, fromDate, toDate));
    },
    [refreshTable, navigate, isEscalation, searchKeyInput, status, onEnquiryTypeChange, fromDate, toDate]
  );

  const handleApplyDateFilter = useCallback(() => {
    if (!validateDates()) {
      return; // Don't apply if validation fails
    }

    setDateFiltersApplied(true);
    if (refreshTable) {
      refreshTable();
    }
    navigate(generateSearchUrl(isEscalation, searchKeyInput, status, enquiryType, fromDate, toDate));
  }, [validateDates, refreshTable, navigate, isEscalation, searchKeyInput, status, enquiryType, fromDate, toDate]);

  const handleClearDateFilter = useCallback(() => {
    setFromDate(null);
    setToDate(null);
    setFromDateError("");
    setToDateError("");

    // Update parent component's refs
    if (onFromDateChange) {
      onFromDateChange(undefined);
    }
    if (onToDateChange) {
      onToDateChange(undefined);
    }

    if (dateFiltersApplied && refreshTable) {
      refreshTable();
    }
    setDateFiltersApplied(false);
    navigate(generateSearchUrl(isEscalation, searchKeyInput, status, enquiryType, null, null));
  }, [
    refreshTable,
    navigate,
    isEscalation,
    searchKeyInput,
    status,
    enquiryType,
    dateFiltersApplied,
    onFromDateChange,
    onToDateChange,
  ]);

  return (
    <>
      <Paper p="xl" sx={{ position: "relative", marginBottom: "20px" }} data-testid="enquiry-search" dir="horizontal">
        <Stack>
          <Group>
            <Group>
              <Text fz="sm">Searching:</Text>
              <Select
                defaultValue={SearchKeywordList.CustomerTickets.toString()}
                data={ticketTypes}
                value={searchType}
                onChange={(val) => {
                  handleSearchTypeChange(val);
                }}
                size="sm"
              />
            </Group>
            <Group>
              <Text fz="sm">Filter by status:</Text>
              <Select
                data={statuses}
                value={status}
                onChange={(val) => {
                  handleStatusChange(val);
                }}
                size="sm"
                sx={{ width: "10rem" }}
              />
            </Group>
            {searchType == SearchKeywordList.CustomerTickets.toString() && (
              <Group>
                <Text fz="sm">Filter by enquiry type:</Text>
                <Select
                  data={enquiryTypes}
                  value={enquiryType}
                  onChange={(val) => {
                    handleEnquiryTypeChange(val);
                  }}
                  size="sm"
                  sx={{ width: "20rem" }}
                />
              </Group>
            )}
          </Group>
          <Stack spacing={5}>
            <Group align="center">
              <Group spacing={10}>
                <Text fz="sm">From:</Text>
                <DateInput
                  placeholder="From Date"
                  w={180}
                  value={fromDate}
                  onChange={(val) => {
                    setFromDate(val);
                    setFromDateError(""); // Clear error on change
                    if (onFromDateChange) {
                      onFromDateChange(val || undefined);
                    }
                  }}
                  clearable
                  error={!!fromDateError}
                />
              </Group>
              <Group spacing={10}>
                <Text fz="sm">To:</Text>
                <DateInput
                  placeholder="To Date"
                  w={180}
                  value={toDate}
                  onChange={(val) => {
                    setToDate(val);
                    setToDateError(""); // Clear error on change
                    if (onToDateChange) {
                      onToDateChange(val || undefined);
                    }
                  }}
                  clearable
                  error={!!toDateError}
                />
              </Group>
              <Anchor
                component="button"
                onClick={handleApplyDateFilter}
                style={{
                  pointerEvents: !fromDate || !toDate ? "none" : "auto",
                  color: !fromDate || !toDate ? theme.colors.gray[5] : undefined,
                  opacity: !fromDate || !toDate ? 0.5 : 1,
                }}
                fz={14}
                fw="bold"
              >
                APPLY
              </Anchor>
              <Anchor
                component="button"
                onClick={handleClearDateFilter}
                style={{
                  pointerEvents: !fromDate && !toDate ? "none" : "auto",
                  color: !fromDate && !toDate ? theme.colors.gray[5] : undefined,
                  opacity: !fromDate && !toDate ? 0.5 : 1,
                }}
                fz={14}
                fw="bold"
              >
                CLEAR
              </Anchor>
            </Group>
            {(fromDateError || toDateError) && (
              <Group spacing={10}>
                <Text size="xs" color="red" ml={50} miw={200}>
                  {fromDateError}
                </Text>
                <Text size="xs" color="red" ml={10}>
                  {toDateError}
                </Text>
              </Group>
            )}
          </Stack>
          <Group>
            <Text fz="sm">Search By Ticket ID/Order Group No/Order No/Customer Name</Text>
            <Group className={classes.searchContainer}>
              <TextInput
                radius="md"
                placeholder="Search"
                size="sm"
                icon={<IconSearch size={16} stroke={1.5} />}
                styles={{ rightSection: { pointerEvents: "none" } }}
                variant="unstyled"
                onChange={handleSearchKeywordChange}
                value={searchKeyword}
              />
              <ActionIcon
                disabled={!searchKeyword || searchKeyword.trim() === ""}
                component="a"
                href={generateSearchUrl(isEscalation, searchKeyInput, status, enquiryType, fromDate, toDate)}
                color="#F04A1C"
                size="lg"
                radius="md"
                variant="filled"
              >
                <IconSearch size="16" />
              </ActionIcon>
            </Group>
          </Group>
        </Stack>
      </Paper>
    </>
  );
};
